/* Каркас экрана: Konsta Page без собственной шапки.
   Заголовок и кнопка «назад» — нативные, из Telegram (шапка мини-аппа). */

import { Page } from "konsta/react";
import React, { useEffect, useRef } from "react";
import Logo from "../assets/logo.svg?react";
import { crumbsOf } from "../lib/crumbs.js";
import { useLocation, useNavigate } from "../lib/router.jsx";
import { haptic, showBackButton } from "../lib/tg.js";

/* Где человек остановился на каждом экране: ушёл вглубь журнала, открыл операцию
   и вернулся — список должен открыться на том же месте, а не с начала. Экраны при
   переходе размонтируются, поэтому позиция живёт здесь, до перезагрузки мини-аппа. */
const scrollMemo = new Map();

/** Сколько ждём, пока экран дорастёт до сохранённой позиции: список приезжает
    из кэша или из сети уже после монтирования, и сразу прокручивать некуда. */
const RESTORE_MS = 1500;

export default function Screen({
  /** Вложенный экран: включает нативную кнопку «Назад» Telegram */
  back = false,
  /** Заголовок в шапке Telegram — между нативными кнопками «назад» и «закрыть» */
  title = null,
  /** Экран первого уровня: вместо заголовка в шапке стоит логотип */
  logo = false,
  /** Свои крошки вместо посчитанных по маршруту; null — не показывать вовсе */
  crumbs,
  children,
  /** Таббар парит над контентом — оставляем место, чтобы низ не уезжал под стекло */
  bottomPad = 132,
  className = "",
}) {
  const navigate = useNavigate();
  const page = useRef(null);
  const { pathname, search } = useLocation();
  const place = pathname + search;

  useEffect(() => {
    if (!back) return undefined;
    const handler = () => {
      haptic.light();
      navigate(-1);
    };
    return showBackButton(handler);
  }, [back, navigate]);

  /* Возврат прокрутки: ждём кадр за кадром, пока страница не станет достаточно
     длинной, и только тогда прыгаем — иначе scrollTop упёрся бы в скелет и остался
     нулём. Пока ждём, чужую прокрутку не запоминаем и от первого же жеста отступаем:
     человек уже сам решил, где смотреть. */
  useEffect(() => {
    const el = page.current;
    if (!el) return undefined;
    const target = scrollMemo.get(place) || 0;
    let restoring = target > 0;
    let raf = 0;
    const stop = () => {
      restoring = false;
      cancelAnimationFrame(raf);
    };

    if (restoring) {
      const until = Date.now() + RESTORE_MS;
      const tick = () => {
        if (!restoring) return;
        if (el.scrollHeight - el.clientHeight >= target) {
          el.scrollTop = target;
          restoring = false;
          return;
        }
        if (Date.now() > until) return stop();
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      el.addEventListener("pointerdown", stop);
      el.addEventListener("wheel", stop, { passive: true });
    }

    const onScroll = () => {
      if (!restoring) scrollMemo.set(place, el.scrollTop);
    };
    el.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      if (!restoring) scrollMemo.set(place, el.scrollTop);
      stop();
      el.removeEventListener("pointerdown", stop);
      el.removeEventListener("wheel", stop);
      el.removeEventListener("scroll", onScroll);
    };
  }, [place]);

  return (
    <Page ref={page} className={className}>
      {/* Заголовок ложится ровно в полосу с нативными кнопками Telegram:
          от верхней безопасной зоны до низа его шапки. Вне Telegram полосы нет —
          держим 44px, чтобы заголовку было где стоять. */}
      {title || logo ? (
        <div
          className="fixed left-0 right-0 z-30 flex items-center justify-center px-20 pointer-events-none"
          style={{
            top: "var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px))",
            height: "max(var(--tg-content-safe-area-inset-top, 0px), 44px)",
          }}
        >
          {logo ? (
            <Logo width={78} height={25} style={{ color: "var(--tg-link)" }} />
          ) : (
            <span className="text-[16px] font-semibold truncate" style={{ color: "var(--tg-text)" }}>
              {title}
            </span>
          )}
        </div>
      ) : null}
      <div
        style={{
          // Шапки нет — верхний отступ держим сами, иначе контент уедет под шапку Telegram
          paddingTop: title || logo
            ? "calc(max(var(--tg-top), 44px) + 12px)"
            : "calc(var(--tg-top) + 12px)",
          paddingBottom: `calc(${bottomPad}px + var(--tg-bottom))`,
        }}
      >
        <Crumbs items={crumbs === undefined ? crumbsOf(pathname) : crumbs} onGo={navigate} />
        {children}
      </div>
    </Page>
  );
}

/* Строка крошек над содержимым: мелко, цветом подписей, разделитель — шеврон.
   Родительские шаги кликаются, текущая страница нет. Одной крошки не бывает:
   вести из неё некуда, и она превратилась бы во второй заголовок. */
function Crumbs({ items, onGo }) {
  if (!items || items.length < 2) return null;

  return (
    <div className="hscroll flex items-center gap-1 px-4 pb-2 text-[12px]">
      {items.map((c, i) => {
        const last = i === items.length - 1;
        return (
          <React.Fragment key={`${c.label}-${i}`}>
            {i > 0 ? (
              <span className="shrink-0" style={{ color: "color-mix(in srgb, var(--tg-hint) 60%, transparent)" }}>
                ›
              </span>
            ) : null}
            {c.to && !last ? (
              <button
                type="button"
                className="shrink-0 whitespace-nowrap active:opacity-60"
                style={{ color: "var(--tg-link)" }}
                onClick={() => { haptic.light(); onGo(c.to); }}
              >
                {c.label}
              </button>
            ) : (
              <span
                className="shrink-0 whitespace-nowrap"
                style={{ color: last ? "var(--tg-hint)" : "var(--tg-link)" }}
              >
                {c.label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
